import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createCustomer, updateCustomer } from '@/services/customers';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Customer } from '@/types/models';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Save, Loader2 } from 'lucide-react';

const customerSchema = z.object({
  name: z.string().min(2, 'Full name must be at least 2 characters').max(120),
  phone: z
    .string()
    .max(30, 'Phone number too long')
    .refine(
      (val) => val === '' || /^[+\d\s\-()]+$/.test(val),
      'Invalid phone number format'
    )
    .optional()
    .or(z.literal('')),
  whatsapp: z
    .string()
    .max(30, 'WhatsApp number too long')
    .refine(
      (val) => val === '' || /^[+\d\s\-()]+$/.test(val),
      'Invalid phone number format'
    )
    .optional()
    .or(z.literal('')),
  invoice_delivery_preference: z.enum(['ask', 'whatsapp', 'email', 'both']).default('ask'),
  email: z.string().email('Invalid email address').max(120).optional().or(z.literal('')),
  address: z.string().max(300, 'Address too long').optional().or(z.literal('')),
  notes: z.string().max(1000, 'Notes too long').optional().or(z.literal('')),
});

type CustomerFormData = z.infer<typeof customerSchema>;

interface CustomerFormProps {
  customer?: Customer;
  onSuccess: () => void;
  onCancel: () => void;
}

function toStringOrEmpty(val: string | null | undefined): string {
  return val ?? '';
}

export const CustomerForm: React.FC<CustomerFormProps> = ({
  customer,
  onSuccess,
  onCancel,
}) => {
  const queryClient = useQueryClient();
  const isEdit = !!customer;
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<CustomerFormData>({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      name: toStringOrEmpty(customer?.name),
      phone: toStringOrEmpty(customer?.phone),
      whatsapp: toStringOrEmpty(customer?.whatsapp),
      invoice_delivery_preference: customer?.invoice_delivery_preference || 'ask',
      email: toStringOrEmpty(customer?.email),
      address: toStringOrEmpty(customer?.address),
      notes: toStringOrEmpty(customer?.notes),
    },
  });

  const mutation = useMutation({
    mutationFn: (data: CustomerFormData) => {
      const payload = {
        name: data.name,
        phone: data.phone || null,
        whatsapp: data.whatsapp || null,
        invoice_delivery_preference: data.invoice_delivery_preference || 'ask',
        email: data.email || null,
        address: data.address || null,
        notes: data.notes || null,
      };
      return isEdit
        ? updateCustomer(customer.id, payload)
        : createCustomer(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      onSuccess();
    },
    onError: (err: unknown) => {
      setSubmitError(parseDatabaseError(err));
    },
  });

  const onSubmit = (data: CustomerFormData) => {
    setSubmitError(null);
    mutation.mutate(data);
  };

  const inputClass = (hasError: boolean) =>
    `w-full px-3 py-2 bg-brand-bg border rounded-md text-sm text-brand-black placeholder-brand-muted focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent transition-all ${
      hasError ? 'border-brand-red focus:ring-brand-red' : 'border-brand-border'
    }`;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      {submitError && <ErrorAlert message={submitError} />}

      {/* Full Name */}
      <div>
        <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
          Full Name <span className="text-brand-red">*</span>
        </label>
        <input
          {...register('name')}
          placeholder="Ahmed Al-Rashid"
          className={inputClass(!!errors.name)}
        />
        {errors.name && (
          <p className="mt-1 text-xs text-brand-red">{errors.name.message}</p>
        )}
      </div>

      {/* Phone, WhatsApp, Email */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            Phone
          </label>
          <input
            {...register('phone')}
            placeholder="+92 300 1234567"
            className={inputClass(!!errors.phone)}
          />
          {errors.phone && (
            <p className="mt-1 text-xs text-brand-red">{errors.phone.message}</p>
          )}
        </div>
        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            WhatsApp Number
          </label>
          <input
            {...register('whatsapp')}
            placeholder="+92 300 1234567"
            className={inputClass(!!errors.whatsapp)}
          />
          {errors.whatsapp && (
            <p className="mt-1 text-xs text-brand-red">{errors.whatsapp.message}</p>
          )}
        </div>
        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            Email
          </label>
          <input
            {...register('email')}
            type="email"
            placeholder="customer@email.com"
            className={inputClass(!!errors.email)}
          />
          {errors.email && (
            <p className="mt-1 text-xs text-brand-red">{errors.email.message}</p>
          )}
        </div>
      </div>

      {/* Invoice Delivery Preference */}
      <div>
        <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
          Invoice Sharing Preference
        </label>
        <select
          {...register('invoice_delivery_preference')}
          className={inputClass(false)}
        >
          <option value="ask">Ask Each Time (Default)</option>
          <option value="whatsapp">Send via WhatsApp</option>
          <option value="email">Send via Email</option>
          <option value="both">Send via Both WhatsApp & Email</option>
        </select>
      </div>

      {/* Address */}
      <div>
        <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
          Address
        </label>
        <textarea
          {...register('address')}
          rows={2}
          placeholder="Shop No. 5, Main Market, Karachi"
          className={`${inputClass(!!errors.address)} resize-none`}
        />
        {errors.address && (
          <p className="mt-1 text-xs text-brand-red">{errors.address.message}</p>
        )}
      </div>

      {/* Notes */}
      <div>
        <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
          Notes
        </label>
        <textarea
          {...register('notes')}
          rows={3}
          placeholder="Internal notes (not visible to customer)..."
          className={`${inputClass(!!errors.notes)} resize-none`}
        />
        {errors.notes && (
          <p className="mt-1 text-xs text-brand-red">{errors.notes.message}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end space-x-3 pt-2 border-t border-brand-border">
        <button
          type="button"
          onClick={onCancel}
          disabled={mutation.isPending}
          className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={mutation.isPending || (isEdit && !isDirty)}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white text-sm font-semibold rounded-md hover:bg-brand-green-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
        >
          {mutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          <span>{mutation.isPending ? 'Saving...' : isEdit ? 'Update Customer' : 'Add Customer'}</span>
        </button>
      </div>
    </form>
  );
};
